/* global gdAlertify */
import React, { useState, useEffect } from "react";
import axiosInstance from "../config/axiosConfig";
import { formatDate, formatMoney } from "../utils/utils";
import "./ReajusteStyles.css";

const Reajuste = () => {
    const [resultados, setResultados] = useState([]);
    const [filtrarReajustados, setFiltrarReajustados] = useState(false);
    const [modificaciones, setModificaciones] = useState({});
    const [busqueda, setBusqueda] = useState("");
    const [loading, setLoading] = useState(false);

    // Fecha desde cuando aplica el reajuste (por defecto, primer día del mes siguiente)
    const getDefaultFechaReajuste = () => {
        const hoy = new Date();
        const primerDiaMesSiguiente = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 1);
        return primerDiaMesSiguiente.toISOString().split('T')[0];
    };
    const [fechaReajuste, setFechaReajuste] = useState(getDefaultFechaReajuste());

    const buscarReajuste = async () => {
        try {
            setLoading(true);
            const params = filtrarReajustados ? { filtrarReajustados: true } : {};
            const response = await axiosInstance.get("/paciente/reajuste", { params });
            setResultados(response.data.data);
        } catch (error) {
            console.error("Error al buscar reajustes: ", error);
            gdAlertify.alertError("Error al cargar los datos");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        buscarReajuste();
    }, [filtrarReajustados]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const datosModificados = Object.entries(modificaciones).map(([rut, cambios]) => ({
            rut,
            ...cambios,
            fechaReajuste: fechaReajuste // Agregar fecha de reajuste
        }));

        if (datosModificados.length === 0) {
            gdAlertify.notify("No hay cambios para guardar", "warning");
            return;
        }

        // Validar que los valores sean positivos
        const valoresInvalidos = datosModificados.filter(d =>
            (d.valorTurnoPaciente && d.valorTurnoPaciente <= 0) ||
            (d.valorTurnoAuxiliar && d.valorTurnoAuxiliar <= 0)
        );
        if (valoresInvalidos.length > 0) {
            gdAlertify.notify("Los valores deben ser mayores a 0", "error");
            return;
        }

        gdAlertify.confirm({
            title: "Confirmar Reajuste",
            message: `¿Está seguro de guardar ${datosModificados.length} reajuste(s) con fecha de aplicación ${formatDate(fechaReajuste)}?`,
            onConfirm: async () => {
                try {
                    setLoading(true);

                    // Ejemplo del JSON que se envía al backend:
                    console.log("📤 Datos enviados al backend:", JSON.stringify({ datos: datosModificados }, null, 2));

                    await axiosInstance.post("/paciente/reajuste/guardar", { datos: datosModificados });
                    gdAlertify.alertSuccess("Reajustes guardados con éxito");
                    buscarReajuste();
                    setModificaciones({});
                } catch (error) {
                    console.error("Error al guardar reajustes: ", error);
                    gdAlertify.alertError("Error al guardar los reajustes");
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const handleInputChange = (rut, campo, valor) => {
        if (valor === "" || valor === null) {
            // Remover el campo específico de modificaciones
            setModificaciones(prev => {
                const newMod = { ...prev };
                if (newMod[rut]) {
                    delete newMod[rut][campo];
                    // Si no quedan campos modificados, remover el rut completo
                    if (Object.keys(newMod[rut]).length === 0) {
                        delete newMod[rut];
                    }
                }
                return newMod;
            });
        } else {
            setModificaciones(prev => ({
                ...prev,
                [rut]: {
                    ...prev[rut],
                    [campo]: parseFloat(valor),
                },
            }));
        }
    };

    const getMesesDesdeReajuste = (fechaReajuste) => {
        if (!fechaReajuste) return null;
        const fecha = new Date(fechaReajuste);
        const hoy = new Date();
        const meses = (hoy.getFullYear() - fecha.getFullYear()) * 12 + (hoy.getMonth() - fecha.getMonth());
        return meses;
    };

    const getBadgeClass = (meses) => {
        if (meses === null) return "badge-neutral";
        if (meses >= 6) return "badge-danger";
        if (meses >= 4) return "badge-warning";
        return "badge-success";
    };

    const getBadgeText = (meses) => {
        if (meses === null) return "Sin registro";
        if (meses === 0) return "Este mes";
        if (meses === 1) return "Hace 1 mes";
        return `Hace ${meses} meses`;
    };

    const datosFiltrados = resultados.filter(row => {
        const searchLower = busqueda.toLowerCase();
        const rut = row.rut || '';
        const nombres = row.nombres || '';

        return rut.includes(busqueda) || nombres.toLowerCase().includes(searchLower);
    });

    const cantidadModificaciones = Object.keys(modificaciones).length;

    return (
        <div className="reajuste-container">
            {/* Header con gradiente */}
            <div className="reajuste-header">
                <div className="header-content">
                    <div className="header-title">
                        <h2>💰 Reajuste de Pacientes</h2>
                        <p>Gestiona los valores de turno de tus pacientes</p>
                    </div>
                    {cantidadModificaciones > 0 && (
                        <div className="modificaciones-badge pulse">
                            <span className="badge-number">{cantidadModificaciones}</span>
                            <span className="badge-text">cambio{cantidadModificaciones > 1 ? 's' : ''} pendiente{cantidadModificaciones > 1 ? 's' : ''}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Controles */}
            <div className="reajuste-controls">
                <div className="controls-row">
                    <div className="search-box">
                        <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.35-4.35" />
                        </svg>
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Buscar por nombre o RUT..."
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                        />
                        {busqueda && (
                            <button className="clear-search" onClick={() => setBusqueda("")}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        )}
                    </div>

                    <div className="fecha-reajuste-box">
                        <label className="fecha-label">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                            Fecha de aplicación:
                        </label>
                        <input
                            type="date"
                            className="fecha-input"
                            value={fechaReajuste}
                            onChange={(e) => setFechaReajuste(e.target.value)}
                        />
                    </div>

                    <div className="filter-toggle">
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={filtrarReajustados}
                                onChange={(e) => setFiltrarReajustados(e.target.checked)}
                            />
                            <span className="toggle-slider"></span>
                        </label>
                        <span className="toggle-label">Solo mayores a 6 meses</span>
                    </div>

                    <button
                        className={`btn-save ${cantidadModificaciones > 0 ? 'active' : ''}`}
                        onClick={handleSubmit}
                        disabled={cantidadModificaciones === 0 || loading}
                    >
                        {loading ? (
                            <>
                                <span className="spinner"></span>
                                Guardando...
                            </>
                        ) : (
                            <>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                                    <polyline points="17 21 17 13 7 13 7 21" />
                                    <polyline points="7 3 7 8 15 8" />
                                </svg>
                                Guardar Cambios
                            </>
                        )}
                    </button>
                </div>

                <div className="results-info">
                    <span className="results-count">
                        {datosFiltrados.length} paciente{datosFiltrados.length !== 1 ? 's' : ''} encontrado{datosFiltrados.length !== 1 ? 's' : ''}
                    </span>
                </div>
            </div>

            {/* Tabla de pacientes */}
            <div className="reajuste-table-container">
                {loading ? (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Cargando datos...</p>
                    </div>
                ) : datosFiltrados.length === 0 ? (
                    <div className="empty-state">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        <h3>No se encontraron pacientes</h3>
                        <p>Intenta ajustar los filtros de búsqueda</p>
                    </div>
                ) : (
                    <div className="table-wrapper">
                        <table className="reajuste-table">
                            <thead>
                                <tr>
                                    <th>Paciente</th>
                                    <th>Último Reajuste</th>
                                    <th>Aviso</th>
                                    <th colSpan="2" className="group-header">Valor Turno Paciente</th>
                                    <th colSpan="2" className="group-header">Valor Turno Auxiliar</th>
                                    <th>Diferencia</th>
                                </tr>
                                <tr>
                                    <th></th>
                                    <th></th>
                                    <th></th>
                                    <th className="sub-header">Actual</th>
                                    <th className="sub-header">Nuevo</th>
                                    <th className="sub-header">Actual</th>
                                    <th className="sub-header">Nuevo</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {datosFiltrados.map((row) => {
                                    const meses = getMesesDesdeReajuste(row.ultimoReajuste);

                                    // Valores Paciente
                                    const valorPacienteModificado = modificaciones[row.rut]?.valorTurnoPaciente;
                                    const valorPacienteActual = row.valorTurnoPaciente;
                                    const diferenciaPaciente = valorPacienteModificado ? valorPacienteModificado - valorPacienteActual : 0;

                                    // Valores Auxiliar
                                    const valorAuxiliarModificado = modificaciones[row.rut]?.valorTurnoAuxiliar;
                                    const valorAuxiliarActual = row.valorTurnoAuxiliar || 0;
                                    const diferenciaAuxiliar = valorAuxiliarModificado ? valorAuxiliarModificado - valorAuxiliarActual : 0;

                                    // Diferencia total
                                    const diferenciaTotal = diferenciaPaciente + diferenciaAuxiliar;
                                    const valorTotalActual = valorPacienteActual + valorAuxiliarActual;
                                    const porcentaje = valorTotalActual > 0 ? ((diferenciaTotal / valorTotalActual) * 100).toFixed(1) : 0;

                                    const hasChange = modificaciones[row.rut] !== undefined;

                                    return (
                                        <tr key={row.rut} className={hasChange ? 'row-modified' : ''}>
                                            <td>
                                                <div className="patient-info-simple">
                                                    <div className="patient-name">{row.nombres}</div>
                                                    <div className="patient-rut">{row.rut}</div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="reajuste-date">
                                                    <span className={`time-badge ${getBadgeClass(meses)}`}>
                                                        {getBadgeText(meses)}
                                                    </span>
                                                    <span className="date-text">{formatDate(row.ultimoReajuste)}</span>
                                                </div>
                                            </td>
                                            <td>
                                                {row.fechaReajusteAvisada ? (
                                                    <div className="reajuste-date">
                                                        <span className="time-badge badge-warning">Avisado</span>
                                                        <span className="date-text">{formatDate(row.fechaReajusteAvisada)}</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>

                                            {/* Valor Turno Paciente - Actual */}
                                            <td>
                                                <div className="valor-actual">
                                                    ${formatMoney(valorPacienteActual)}
                                                </div>
                                            </td>

                                            {/* Valor Turno Paciente - Nuevo */}
                                            <td>
                                                <div className="input-wrapper">
                                                    <span className="currency-symbol">$</span>
                                                    <input
                                                        type="number"
                                                        className={`valor-input ${valorPacienteModificado ? 'modified' : ''}`}
                                                        placeholder={valorPacienteActual}
                                                        value={valorPacienteModificado || ''}
                                                        onChange={(e) => handleInputChange(row.rut, 'valorTurnoPaciente', e.target.value)}
                                                        min="0"
                                                        step="1000"
                                                    />
                                                    {valorPacienteModificado && (
                                                        <button
                                                            className="clear-btn"
                                                            onClick={() => handleInputChange(row.rut, 'valorTurnoPaciente', null)}
                                                            title="Limpiar cambio"
                                                        >
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                                <line x1="18" y1="6" x2="6" y2="18" />
                                                                <line x1="6" y1="6" x2="18" y2="18" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Valor Turno Auxiliar - Actual */}
                                            <td>
                                                <div className="valor-actual">
                                                    ${formatMoney(valorAuxiliarActual)}
                                                </div>
                                            </td>

                                            {/* Valor Turno Auxiliar - Nuevo */}
                                            <td>
                                                <div className="input-wrapper">
                                                    <span className="currency-symbol">$</span>
                                                    <input
                                                        type="number"
                                                        className={`valor-input ${valorAuxiliarModificado ? 'modified' : ''}`}
                                                        placeholder={valorAuxiliarActual}
                                                        value={valorAuxiliarModificado || ''}
                                                        onChange={(e) => handleInputChange(row.rut, 'valorTurnoAuxiliar', e.target.value)}
                                                        min="0"
                                                        step="1000"
                                                    />
                                                    {valorAuxiliarModificado && (
                                                        <button
                                                            className="clear-btn"
                                                            onClick={() => handleInputChange(row.rut, 'valorTurnoAuxiliar', null)}
                                                            title="Limpiar cambio"
                                                        >
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                                <line x1="18" y1="6" x2="6" y2="18" />
                                                                <line x1="6" y1="6" x2="18" y2="18" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Diferencia Total */}
                                            <td>
                                                {hasChange && diferenciaTotal !== 0 && (
                                                    <div className={`diferencia ${diferenciaTotal >= 0 ? 'positive' : 'negative'}`}>
                                                        <span className="diff-amount">
                                                            {diferenciaTotal >= 0 ? '+' : ''}${formatMoney(Math.abs(diferenciaTotal))}
                                                        </span>
                                                        <span className="diff-percent">
                                                            ({diferenciaTotal >= 0 ? '+' : ''}{porcentaje}%)
                                                        </span>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Reajuste;
